'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, X, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/shared/ui/shadcn/tabs';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';

interface GoalsEnhanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalGoals: string;
  enhancedGoals: string;
  onAccept: (enhancedGoals: string) => void;
  isLoading?: boolean;
}

export function GoalsEnhanceModal({
  isOpen,
  onClose,
  originalGoals,
  enhancedGoals,
  onAccept,
  isLoading = false,
}: GoalsEnhanceModalProps) {
  const t = useTranslations('goalsEnhance');
  const [selectedVersion, setSelectedVersion] = useState<
    'original' | 'enhanced'
  >('enhanced');

  const handleAccept = () => {
    onAccept(enhancedGoals);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Version selector using Tabs */}
          <Tabs
            value={selectedVersion}
            onValueChange={(value) =>
              setSelectedVersion(value as 'original' | 'enhanced')
            }
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="original">{t('tabs.original')}</TabsTrigger>
              <TabsTrigger value="enhanced" className="flex items-center gap-1">
                <Sparkles className="w-4 h-4" />
                {t('tabs.enhanced')}
              </TabsTrigger>
            </TabsList>

            {/* Text display */}
            <div className="min-h-[150px] mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-[150px] bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('loading')}
                  </div>
                </div>
              ) : (
                <>
                  <TabsContent value="original" className="mt-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="text-section-eyebrow text-muted-foreground mb-2">
                        {t('originalHeading')}
                      </h4>
                      <p className="whitespace-pre-wrap">{originalGoals}</p>
                    </div>
                  </TabsContent>

                  <TabsContent value="enhanced" className="mt-4">
                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <h4 className="text-section-eyebrow text-primary mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {t('enhancedHeading')}
                      </h4>
                      <p className="whitespace-pre-wrap font-medium">
                        {enhancedGoals}
                      </p>
                    </div>
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>

          {/* Information note */}
          <NoticeBanner
            variant="info"
            message={t('infoNote')}
            icon={Sparkles}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            {t('keepOriginal')}
          </Button>
          <Button
            onClick={handleAccept}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            {t('useEnhanced')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
